import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useConfirmStore } from '../store/confirmStore';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  fetchConnectivityGraph,
  fetchSpofAnalysis,
  fetchBandwidthAnalysis,
  createCable,
  deleteCable,
} from '../utils/connectivityApi';

export function useConnectivityData(dcCode: string | undefined) {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspace();

  const { data: graph } = useQuery({
    queryKey: ['connectivity-graph', dcCode, activeWorkspaceId],
    queryFn: () => fetchConnectivityGraph(dcCode!, activeWorkspaceId),
    enabled: !!dcCode,
  });

  const { data: spof } = useQuery({
    queryKey: ['connectivity-spof', dcCode, activeWorkspaceId],
    queryFn: () => fetchSpofAnalysis(dcCode!, activeWorkspaceId),
    enabled: !!dcCode,
  });

  const { data: bandwidth } = useQuery({
    queryKey: ['connectivity-bandwidth', dcCode, activeWorkspaceId],
    queryFn: () => fetchBandwidthAnalysis(dcCode!, activeWorkspaceId),
    enabled: !!dcCode,
  });

  const handleCreateCable = useCallback(async (cableData: any) => {
    if (!dcCode) return false;
    try {
      await createCable(dcCode, cableData, activeWorkspaceId);
      queryClient.invalidateQueries({ queryKey: ['connectivity-graph', dcCode, activeWorkspaceId] });
      return true;
    } catch (err: any) {
      console.error(err);
      return false;
    }
  }, [dcCode, queryClient, activeWorkspaceId]);

  const handleDeleteCable = useCallback(async (cableId: string) => {
    if (!dcCode) return false;
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление кабеля',
      message: 'Удалить кабель?',
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (!confirmed) return false;
    try {
      await deleteCable(dcCode, cableId, activeWorkspaceId); // Backend takes care of locating the cable, but query needs update
      queryClient.invalidateQueries({ queryKey: ['connectivity-graph', dcCode, activeWorkspaceId] });
      return true;
    } catch (err: any) {
      console.error(err);
      return false;
    }
  }, [dcCode, queryClient, activeWorkspaceId]);

  return {
    graph,
    spof,
    bandwidth,
    handleCreateCable,
    handleDeleteCable,
  };
}
